/**
 * 投放词库表格列配置
 * 限制: <150行
 */
import React from 'react';
import { Tag, InputNumber, Switch, Space, Tooltip, Button, Popconfirm, Form } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { FormInstance } from 'antd';
import {
    TargetingKeyword,
    KEYWORD_TYPE_OPTIONS,
    MATCH_TYPE_OPTIONS,
    KEYWORD_TYPE_COLORS,
    KeywordType,
    MatchType,
} from './types';


interface GetColumnsParams {
    editingId: number | null;
    editForm: FormInstance;
    onUpdate: (id: number, data: Partial<TargetingKeyword>) => Promise<any>;
    onDelete: (id: number) => Promise<any>;
    onEdit: (record: TargetingKeyword) => void;
}

export const getTargetingKeywordColumns = ({
    editingId,
    editForm,
    onUpdate,
    onDelete,
    onEdit,
}: GetColumnsParams): ColumnsType<TargetingKeyword> => [
        {
            title: '关键词',
            dataIndex: 'keyword',
            key: 'keyword',
            width: 200,
            ellipsis: true,
            render: (text: string) => (
                <Tooltip title={text}>
                    <span style={{ fontWeight: 500 }}>{text}</span>
                </Tooltip>
            ),
        },
        {
            title: '词性',
            dataIndex: 'keyword_type',
            key: 'keyword_type',
            width: 120,
            render: (type: KeywordType) => (
                <Tag color={KEYWORD_TYPE_COLORS[type]}>
                    {KEYWORD_TYPE_OPTIONS[type]}
                </Tag>
            ),
        },
        {
            title: '匹配',
            dataIndex: 'match_type',
            key: 'match_type',
            width: 90,
            render: (type: MatchType) => <Tag>{MATCH_TYPE_OPTIONS[type]}</Tag>,
        },
        {
            title: '转化率',
            dataIndex: 'conversion_rate',
            key: 'conversion_rate',
            width: 80,
            align: 'right',
            render: (val: number | null) => val ? `${(val * 100).toFixed(2)}%` : '-',
        },
        {
            title: '搜索量',
            dataIndex: 'search_volume',
            key: 'search_volume',
            width: 90,
            align: 'right',
            render: (val: number | null) => val?.toLocaleString() || '-',
        },
        {
            title: '竞争度',
            dataIndex: 'competition_level',
            key: 'competition_level',
            width: 70,
            align: 'center',
            render: (val: number | null) => val || '-',
        },
        {
            title: '预估CPC',
            dataIndex: 'estimated_cpc',
            key: 'estimated_cpc',
            width: 90,
            align: 'right',
            render: (val: number | null) => val ? `$${Number(val).toFixed(2)}` : '-',
        },
        {
            title: '优先级',
            dataIndex: 'priority',
            key: 'priority',
            width: 70,
            align: 'center',
            render: (val: number, record: TargetingKeyword) => {
                if (editingId === record.id) {
                    return (
                        <InputNumber
                            size="small"
                            min={1}
                            max={5}
                            defaultValue={val}
                            onChange={(v) => editForm.setFieldValue('priority', v)}
                            style={{ width: 50 }}
                        />
                    );
                }
                return (
                    <Tag color={val >= 4 ? 'green' : val >= 2 ? 'blue' : 'default'}>
                        {val}
                    </Tag>
                );
            },
        },
        {
            title: '状态',
            dataIndex: 'is_active',
            key: 'is_active',
            width: 70,
            align: 'center',
            render: (active: boolean, record: TargetingKeyword) => (
                <Switch
                    size="small"
                    checked={active}
                    onChange={(checked) => onUpdate(record.id, { is_active: checked })}
                />
            ),
        },
        {
            title: '操作',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_: any, record: TargetingKeyword) => (
                <Space size="small">
                    <Tooltip title="编辑">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => onEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定删除?"
                        onConfirm={() => onDelete(record.id)}
                    >
                        <Tooltip title="删除">
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];
